import { memo } from 'react';
import ShieldIcon from '@mui/icons-material/Shield';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { readableDropDareDifficultyLevels } from 'root/src/shared/constants/dropEvent';
import { DareCta, DARE_DIFFICULTY_CLASS, DlcChip, ClipsOnlyChip, dareRewardName, isDlcDareReward, isClipsOnlyDare } from 'root/src/client/v2/_pages/drop/redesign/dareCta';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { upscaleBoxArt } from 'root/src/client/v2/_pages/drops/redesign/dropImage';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { RewardAmountBadge } from 'root/src/client/v2/common/components/RewardAmountBadge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
import { DareDetailsPanel, useDareHoverDetails } from 'root/src/client/v2/_pages/drop/redesign/DareDetailsPanel';
// The list-view sibling of RedesignDareCard: one compact row per dare with the same CTA (the shared
// DareCta keeps accept/deliver/blocked behaviour identical across views). flex-wrap + a full-width
// action column below `sm` lets the row break into two lines on phones instead of overflowing;
// min-w-0 on the text block keeps long titles truncating rather than stretching the row.
export const RedesignDareRow = memo(({ drop, dare, refetchDropEvent }) => {
    var _a, _b, _c, _d, _e, _f;
    // Same art as the card, requested at thumbnail size.
    const image = buildAssetUrl(upscaleBoxArt((_c = (_b = (_a = dare.games) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.image) !== null && _c !== void 0 ? _c : (_d = drop.gameObj) === null || _d === void 0 ? void 0 : _d.image, '600x800'), { w: 200, q: 80, fmt: 'webp' });
    const rewardName = dareRewardName(dare);
    const isDlc = isDlcDareReward(dare);
    // The row truncates harder than the card (one line of description), so it earns the same hover
    // panel; it opens below rather than to the side, since a row spans the full width.
    const { showDetails, onHintOpenChange, hoverProps } = useDareHoverDetails(dare);
    return (<TooltipProvider delayDuration={150}>
			<article {...hoverProps} className="group flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-solid border-white/[0.06] bg-card p-3 transition-colors hover:border-neon-pink/30">
				<div className="relative hidden h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white/[0.03] sm:block">
					{image && <Img src={image} alt={dare.title} loading="lazy" className="h-full w-full object-cover"/>}
				</div>
				<Tooltip open={showDetails}>
					<TooltipTrigger asChild>
						<div className="min-w-0 flex-1 basis-48">
							<div className="flex min-w-0 items-center gap-2">
								<h4 className="min-w-0 truncate text-sm font-bold text-foreground">{dare.title}</h4>
								<span className={cn('inline-flex shrink-0 items-center gap-1 rounded-md border border-solid bg-black/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider', DARE_DIFFICULTY_CLASS)}>
									<ShieldIcon sx={{ fontSize: 12 }}/>
									{(_e = readableDropDareDifficultyLevels[dare.level]) !== null && _e !== void 0 ? _e : dare.level}
								</span>
							</div>
							<p className="mt-0.5 truncate text-xs text-muted-foreground">{dare.description}</p>
						</div>
					</TooltipTrigger>
					<TooltipContent side="bottom" align="start" className="max-w-[22rem] p-3">
						<DareDetailsPanel dare={dare}/>
					</TooltipContent>
				</Tooltip>
				{/* Platforms and the clips-only chip are one flex item, not two: the chip belongs with the
        Twitch icon it qualifies, and keeping the row's item count unchanged leaves the existing
        wrapping behaviour alone. */}
				{((_f = dare.platforms) === null || _f === void 0 ? void 0 : _f.length) > 0 && (<div className="flex shrink-0 items-center gap-2">
						<ul className="flex items-center gap-1.5">
							{dare.platforms.map(platform => (<li key={platform} className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
									<DarePlatformIcon size="12" platform={platform} color={PlatformIconColor.Dark}/>
								</li>))}
						</ul>
						{isClipsOnlyDare(dare) && <ClipsOnlyChip onOpenChange={onHintOpenChange}/>}
					</div>)}
				<Tooltip onOpenChange={onHintOpenChange}>
					<TooltipTrigger asChild>
						{/* Tooltip carries the untruncated reward name (and total) for narrow rows. */}
						<div className="flex min-w-0 max-w-[220px] cursor-help items-center gap-1.5">
							<CardGiftcardIcon sx={{ fontSize: 15 }} className={cn('shrink-0', isDlc ? 'text-neon-teal' : 'text-neon-green')}/>
							<span className="min-w-0 truncate text-xs font-medium text-foreground/90">{rewardName}</span>
							{isDlc && <DlcChip />}
							<RewardAmountBadge amount={dare.amount}/>
						</div>
					</TooltipTrigger>
					<TooltipContent className="max-w-[240px]">{rewardName}</TooltipContent>
				</Tooltip>
				<div className="w-full shrink-0 sm:w-44">
					<DareCta drop={drop} dare={dare} refetchDropEvent={refetchDropEvent}/>
				</div>
			</article>
		</TooltipProvider>);
});
